import { useMemo, useState } from "react";
import { stepLabel } from "../stepLabel.js";
import { BLOCKS_PER_DAY, blockToDate, dateToBlock } from "../../domain/blocks.js";
import { timeOffSpans } from "../../domain/absence.js";
import type { Board, ScheduledRow } from "../api.js";

export const ZOOMS = [2, 4, 8, 16] as const;
export type Zoom = (typeof ZOOMS)[number];

function laneClass(p: string, unassigned: boolean): string {
  if (unassigned) return "una";
  return p === "Expedite" ? "exp" : p === "Low" ? "low" : "std";
}

/** The four states worth seeing at a glance on a bar; any other state leaves the bar as it was. */
const STATUS_CLASS: Record<string, string> = {
  Active: "st-active", Hold: "st-hold", Checking: "st-checking", Corrections: "st-corrections",
};
const statusClass = (state: string | undefined): string => (state && STATUS_CLASS[state]) ? ` ${STATUS_CLASS[state]}` : "";
const statusTitle = (state: string | undefined): string => (state && STATUS_CLASS[state]) ? ` · ${state}` : "";

/** The color key shared by both schedule views. */
function StatusKey() {
  return (
    <span className="g-key">
      Bar top edge:
      <i className="sw st-active" />Active
      <i className="sw st-hold" />Hold
      <i className="sw st-checking" />Checking
      <i className="sw st-corrections" />Corrections
    </span>
  );
}

function weekLabels(fromBlock: number, weeks: number): string[] {
  const out: string[] = [];
  for (let w = 0; w < weeks; w++) {
    const { date } = blockToDate(fromBlock + w * 10);
    const d = new Date(`${date}T00:00:00Z`);
    out.push(d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }));
  }
  return out;
}

/**
 * Place a bar in the visible window, clipping to it. Returns null if outside.
 * `clippedLeft`/`clippedRight` let the bar show that it continues past the edge
 * — at 2-week zoom most bars run off, and a bar that just stops is a lie.
 */
function place(
  a: ScheduledRow,
  fromBlock: number,
  cols: number,
): { start: number; span: number; clippedLeft: boolean; clippedRight: boolean } | null {
  if (a.startBlock === null || a.endBlock === null) return null;
  const rawS = a.startBlock - fromBlock;
  const rawE = a.endBlock - fromBlock;
  const s = Math.max(rawS, 0);
  const e = Math.min(rawE, cols - 1);
  if (e < 0 || s > cols - 1) return null;
  return { start: s + 1, span: Math.max(e - s + 1, 1), clippedLeft: rawS < 0, clippedRight: rawE > cols - 1 };
}

export interface Due {
  /** 1-based grid column of the marker, or null when the date is off-window. */
  col: number | null;
  late: boolean;
  daysLate: number;
  /** True when the date has already passed — there is no column to the left. */
  behind: boolean;
  date: string;
}

/**
 * Where a phase's required date falls in the visible window, and whether the
 * schedule keeps it.
 *
 * The board always starts at today (`fromBlock = dateToBlock(today)`), so a
 * date already missed has no column to sit in — it is off the left edge, and
 * that is precisely the case worth showing. It comes back as `behind` with the
 * days owed, for the bar to carry, rather than as nothing at all.
 */
export function due(
  reqDueDate: string | null | undefined,
  endBlock: number | null,
  fromBlock: number,
  cols: number,
): Due | null {
  if (!reqDueDate) return null;
  // The promise is "finished by the end of that day", so the last block still
  // on time is the due date's PM — not its AM.
  const lastOnTime = dateToBlock(reqDueDate) + 1;
  const offset = lastOnTime - fromBlock;
  const behind = offset < 0;
  // Unassigned work has no end block. A passed date still makes it late — more
  // so, in fact, since nothing is scheduled to clear it.
  const late = behind || (endBlock !== null && endBlock > lastOnTime);
  const overshoot = (endBlock !== null ? endBlock : fromBlock) - lastOnTime;
  return {
    col: offset >= 0 && offset < cols ? offset + 1 : null,
    late,
    daysLate: late ? Math.max(Math.ceil(overshoot / BLOCKS_PER_DAY), 1) : 0,
    behind,
    date: reqDueDate,
  };
}

/**
 * The tooltip a marker and its bar share, so both say the same thing.
 * "Finishes late" and "was due and is not scheduled" are different failures
 * and a scheduler acts on them differently, so they read differently.
 */
function dueTitle(d: Due, scheduled: boolean, extra?: DueExtra): string {
  const tail = [
    extra?.finish ? `scheduled finish ${extra.finish}` : null,
    extra?.projectDue ? `project due ${extra.projectDue}` : null,
  ].filter(Boolean).join(" · ");
  const more = tail ? ` · ${tail}` : "";
  if (d.behind && !scheduled) return `step required ${d.date} — ${d.daysLate}d overdue, not scheduled${more}`;
  if (d.late) return `step required ${d.date} — finishes ${d.daysLate}d late${more}`;
  return `step required ${d.date} — on time${more}`;
}

/** What else the hover names, so the line can be tied back to its step: where the step ends and when the project is due. */
interface DueExtra { finish?: string | null; projectDue?: string | null }

/** Group phases of a project by family — DetM-1..DetM-11 collapse to "DetM". */
export function familyOf(phaseCode: string | null | undefined): string {
  if (!phaseCode) return "other";
  const m = /^([A-Za-z]+)/.exec(phaseCode.trim());
  return m ? m[1]! : phaseCode.trim();
}

interface Props {
  board: Board;
  zoom: Zoom;
  onMove: (assignmentId: string, patch: { resourceId?: string | null; queuePosition?: number }) => void;
  /**
   * True once the caller has already narrowed `board.resources` to a team or
   * a person (see `identityFiltered` in Filters.tsx) — in which case everyone
   * left belongs on screen, including someone with nothing assigned right
   * now. Left false, a resource with no items is dropped, which is what keeps
   * an unfiltered schedule from listing the whole roster.
   */
  keepIdleResources?: boolean;
  /**
   * Double-clicking a bar opens its project and all its steps. A single click
   * is already taken: pressing on a bar is how it is picked up and dragged.
   */
  onOpenProject?: (projectId: string) => void;
  /**
   * Lock a person's order, or unlock it. Offered only where the caller can do it —
   * in a draft, for someone the viewer manages — so `can` is asked per person.
   */
  lock?: { can: (resourceId: string) => boolean; set: (resourceId: string, locked: boolean) => void };
}

export function GanttByResource({ board, zoom, onMove, keepIdleResources = false, onOpenProject, lock }: Props) {
  const [dragging, setDragging] = useState<string | null>(null);
  const cols = zoom * 10;
  const phases = useMemo(() => new Map(board.phases.map((p) => [p.phaseId, p])), [board.phases]);
  const projectDueOf = useMemo(() => new Map(board.projects.map((p) => [p.projectId, p.committedDate])), [board.projects]);
  const extraFor = (a: ScheduledRow): DueExtra => ({
    finish: a.endDate ?? null, projectDue: projectDueOf.get(phases.get(a.phaseId)?.projectId ?? "") ?? null,
  });
  const labels = useMemo(() => weekLabels(board.fromBlock, zoom), [board.fromBlock, zoom]);

  const byResource = useMemo(() => {
    const m = new Map<string, ScheduledRow[]>();
    for (const a of board.assignments) {
      const key = a.resourceId ?? "__pool__";
      (m.get(key) ?? m.set(key, []).get(key)!).push(a);
    }
    for (const list of m.values()) list.sort((x, y) => (x.queuePosition ?? 0) - (y.queuePosition ?? 0));
    return m;
  }, [board.assignments]);

  // Time off by person, ready to draw as blocks on their row.
  const offByResource = useMemo(() => {
    const m = new Map<string, Board["timeOff"]>();
    for (const t of board.timeOff ?? []) (m.get(t.resourceId) ?? m.set(t.resourceId, []).get(t.resourceId)!).push(t);
    return m;
  }, [board.timeOff]);

  // Pressure, not utilization: "can the work promised in the next four weeks
  // fit in four weeks". A derived schedule is never over 100% by construction.
  const utilByResource = useMemo(
    () => new Map(board.pressure.map((p) => [p.resourceId, p.pct])),
    [board.pressure],
  );

  // Busiest first — the rows a scheduler needs are at the top.
  const rows = [...board.resources]
    .filter((r) => !r.isPool)
    .map((r) => ({ resource: r, items: byResource.get(r.resourceId) ?? [] }))
    .filter((r) => keepIdleResources || r.items.length > 0)
    .sort((a, b) => (utilByResource.get(b.resource.resourceId) ?? 0) - (utilByResource.get(a.resource.resourceId) ?? 0));

  // Work parked on a pool placeholder (Drafting-Mechanical / -Electrical) is
  // unowned in every sense that matters, so it belongs in the pool row too —
  // otherwise it is filtered out of the resource rows and vanishes entirely.
  const poolResourceIds = new Set(board.resources.filter((r) => r.isPool).map((r) => r.resourceId));
  const pool = board.assignments.filter((a) => !a.resourceId || poolResourceIds.has(a.resourceId));

  const drop = (resourceId: string | null) => (e: React.DragEvent) => {
    e.preventDefault();
    if (!dragging) return;
    const target = byResource.get(resourceId ?? "__pool__") ?? [];
    const last = target.reduce((n, a) => Math.max(n, a.queuePosition ?? 0), 0);
    onMove(dragging, { resourceId, queuePosition: last + 1 });
    setDragging(null);
  };

  return (
    <div className="scroll gantt-scroll">
      <div className="gantt" style={{ ["--cols" as string]: cols }}>
        <div className="g-head">
          <div className="g-corner">Resource · 4wk pressure</div>
          {labels.map((l) => (
            <div className="g-wk" key={l}>{l}</div>
          ))}
        </div>

        {rows.map(({ resource, items }) => {
          const pct = utilByResource.get(resource.resourceId) ?? 0;
          const over = pct > 1;
          // A step at or past its estimate has no hours left, so it has no bar.
          // It is still open, so the row says so rather than letting it vanish.
          const overSteps = items.filter((a) => phases.get(a.phaseId)?.overEstimate);
          return (
            <div
              className={`g-row${over ? " over" : ""}`}
              key={resource.resourceId}
              onDragOver={(e) => e.preventDefault()}
              onDrop={drop(resource.resourceId)}
            >
              <div className="g-name">
                <b>{resource.name}</b>
                <span>
                  {resource.disciplineGroup?.replace("01-", "")} ·{" "}
                  <span className={`g-util${over ? " over" : pct < 0.4 ? " under" : ""}`}>
                    {Math.round(pct * 100)}%
                  </span>
                </span>
                {lock && items.length > 0 && lock.can(resource.resourceId) && (() => {
                  const locked = items.every((a) => a.pinned);
                  return (
                    <button
                      className={`g-lock${locked ? " on" : ""}`}
                      title={locked
                        ? `${resource.name}'s order is locked: their work and its order hold through every solve in this draft, and after it is published. Click to unlock.`
                        : `Lock ${resource.name}'s order as it is now, so no solve moves their work or changes its order.`}
                      onClick={() => lock.set(resource.resourceId, !locked)}
                    >
                      {locked ? "🔒 order locked" : "🔓 lock order"}
                    </button>
                  );
                })()}
                {overSteps.length > 0 && (
                  <span
                    className="g-over"
                    title={`At or past their estimate and still open, so no bar: ${overSteps.map((a) => stepLabel(phases.get(a.phaseId), a.phaseId)).join(", ")}. Re-estimate or complete them.`}
                  >
                    ⚑ {overSteps.length} over estimate
                  </span>
                )}
              </div>
              <div className="g-lane">
                {timeOffSpans(offByResource.get(resource.resourceId) ?? [], board.fromBlock, cols).map((o) => (
                  <div
                    key={`off-${o.start}`}
                    className="g-off"
                    style={{ gridColumn: `${o.start} / span ${o.span}` }}
                    title={(() => {
                      const first = blockToDate(board.fromBlock + o.start - 1).date;
                      const last = blockToDate(board.fromBlock + o.start + o.span - 2).date;
                      return `${resource.name} is out${o.halfOnly ? " (half day)" : ""}, ${first === last ? first : `${first} to ${last}`}`;
                    })()}
                  />
                ))}
                {items.map((a) => {
                  const pos = place(a, board.fromBlock, cols);
                  if (!pos) return null;
                  const phase = phases.get(a.phaseId);
                  const d = due(phase?.reqDueDate, a.endBlock, board.fromBlock, cols);
                  return (
                    <div
                      key={a.assignmentId}
                      className={`g-bar ${laneClass(a.priority, false)}${statusClass(phase?.workflowState)}${dragging === a.assignmentId ? " dragging" : ""}${a.frozen ? " frozen" : ""}${pos.clippedLeft ? " clip-l" : ""}${pos.clippedRight ? " clip-r" : ""}${d?.late ? " late" : ""}`}
                      style={{ gridColumn: `${pos.start} / span ${pos.span}` }}
                      draggable={!a.frozen}
                      onDragStart={() => setDragging(a.assignmentId)}
                      onDragEnd={() => setDragging(null)}
                      onDoubleClick={phase && onOpenProject ? () => onOpenProject(phase.projectId) : undefined}
                      title={`${stepLabel(phase, a.phaseId)} · ${phase?.description ?? ""} · ${phase?.remainingHours ?? 0}h remaining${a.frozen ? " · in progress, frozen" : ""}${statusTitle(phase?.workflowState)}${d ? ` · ${dueTitle(d, true, extraFor(a))}` : ""}${onOpenProject ? " · double-click to open the project" : ""}`}
                    >
                      {/* The glyph, not the count — at 8-week zoom a bar is ~50px
                          wide and the days are in the tooltip either way. */}
                      {d?.late ? "⚠ " : ""}{stepLabel(phase, a.phaseId)} · {phase?.remainingHours ?? 0}h
                    </div>
                  );
                })}
                {/* Only the missed dates, and after the bars so they draw on
                    top of them. A lane holds a dozen phases; a marker for every
                    one of them was a row of ticks with no way to tell which bar
                    each belonged to. Dates the schedule keeps are in the bar's
                    own tooltip, where the association is unambiguous. */}
                {items.map((a) => {
                  const d = due(phases.get(a.phaseId)?.reqDueDate, a.endBlock, board.fromBlock, cols);
                  if (!d || d.col === null || !d.late) return null;
                  return (
                    <div
                      key={`due-${a.assignmentId}`}
                      className="g-due"
                      style={{ gridColumn: d.col }}
                      title={`${stepLabel(phases.get(a.phaseId), a.phaseId)} — ${dueTitle(d, true, extraFor(a))}`}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}

        <div className="g-row" onDragOver={(e) => e.preventDefault()} onDrop={drop(null)}>
          <div className="g-name">
            <b>Unassigned pool</b>
            <span>{pool.length} phases · needs an owner</span>
          </div>
          <div className="g-lane">
            {pool.slice(0, 14).map((a, i) => {
              const phase = phases.get(a.phaseId);
              const span = Math.max(Math.min(a.halfDayBlocks, Math.floor(cols / 4)), 2);
              // No due marker on this row: these bars sit at decorative
              // positions, so a date drawn against them would be a claim the
              // row cannot make. The bar still carries whether it is overdue.
              const d = due(phase?.reqDueDate, a.endBlock, board.fromBlock, cols);
              return (
                <div
                  key={a.assignmentId}
                  className={`g-bar una${statusClass(phase?.workflowState)}${d?.late ? " late" : ""}`}
                  style={{ gridColumn: `${1 + ((i * 6) % Math.max(cols - span, 1))} / span ${span}` }}
                  draggable
                  onDragStart={() => setDragging(a.assignmentId)}
                  onDragEnd={() => setDragging(null)}
                  onDoubleClick={phase && onOpenProject ? () => onOpenProject(phase.projectId) : undefined}
                  title={`${stepLabel(phase, a.phaseId)} · drag onto a resource row to assign${d ? ` · ${dueTitle(d, a.endBlock !== null, extraFor(a))}` : ""}${onOpenProject ? " · double-click to open the project" : ""}`}
                >
                  {d?.late ? "⚠ " : ""}{stepLabel(phase, a.phaseId)} · {phase?.remainingHours ?? 0}h
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="legend">
        <span>Drag a bar onto another row to reassign — dates recalculate</span>
        <span>Left edge red = promised more in the next 4 weeks than fits</span>
        <span>Frozen bars are work in progress and cannot be dragged</span>
        <span>
          <b>Red dashed line</b> = the step's required date, drawn only where this schedule misses it. The bar it
          belongs to is underlined red and marked ⚠. Hover the line or the bar for the step's required date,
          its scheduled finish and the project's due date.
        </span>
        <span>The window starts today, so a date already gone has no line — only the ⚠ on the bar</span>
        <span>Dates the schedule keeps are on the bar's tooltip, not drawn</span>
        <span><i className="sw off" />Hatched block = time off (a half-width block is a half day)</span>
        <span>⚑ over estimate = open steps with no hours left, so no bar; see the Backlog</span>
        <StatusKey />
      </div>
    </div>
  );
}

export function GanttByProject(
  { board, zoom, onOpenProject }: { board: Board; zoom: Zoom; onOpenProject?: (projectId: string) => void },
) {
  const cols = zoom * 10;
  const phases = useMemo(() => new Map(board.phases.map((p) => [p.phaseId, p])), [board.phases]);
  const resources = useMemo(() => new Map(board.resources.map((r) => [r.resourceId, r.name])), [board.resources]);
  const projectDueOf = useMemo(() => new Map(board.projects.map((p) => [p.projectId, p.committedDate])), [board.projects]);
  const labels = useMemo(() => weekLabels(board.fromBlock, zoom), [board.fromBlock, zoom]);
  const [openProjects, setOpenProjects] = useState<Set<string>>(new Set());
  const [openFamilies, setOpenFamilies] = useState<Set<string>>(new Set());

  const byProject = useMemo(() => {
    const m = new Map<string, ScheduledRow[]>();
    for (const a of board.assignments) {
      const p = phases.get(a.phaseId);
      if (!p) continue;
      (m.get(p.projectId) ?? m.set(p.projectId, []).get(p.projectId)!).push(a);
    }
    return m;
  }, [board.assignments, phases]);

  const rows = board.projects
    .map((project) => {
      const items = byProject.get(project.projectId) ?? [];
      const remaining = items.reduce((n, a) => n + (phases.get(a.phaseId)?.remainingHours ?? 0), 0);
      const due = items.map((a) => phases.get(a.phaseId)?.reqDueDate).filter(Boolean).sort()[0] as string | undefined;
      // Three levels: project -> phase family (DetM, SubE...) -> the phases.
      // 218023 has 11 DetM steps; without this the board is unreadable.
      const families = new Map<string, ScheduledRow[]>();
      for (const a of items) {
        const f = familyOf(phases.get(a.phaseId)?.phaseCode);
        (families.get(f) ?? families.set(f, []).get(f)!).push(a);
      }
      return { project, items, remaining, due, families };
    })
    // A project whose only open steps are over their estimate has no hours left but is not done.
    .filter((r) => r.remaining > 0 || r.items.some((a) => phases.get(a.phaseId)?.overEstimate))
    .sort((a, b) => b.remaining - a.remaining);

  const toggle = (set: Set<string>, k: string, fn: (s: Set<string>) => void) => {
    const n = new Set(set);
    n.has(k) ? n.delete(k) : n.add(k);
    fn(n);
  };

  const envelope = (items: ScheduledRow[]) => {
    const placed = items.map((a) => place(a, board.fromBlock, cols)).filter(Boolean) as NonNullable<ReturnType<typeof place>>[];
    if (!placed.length) return null;
    const first = Math.min(...placed.map((p) => p.start));
    const last = Math.max(...placed.map((p) => p.start + p.span - 1));
    return { first, span: last - first + 1 };
  };

  return (
    <div className="scroll gantt-scroll">
      <div className="gantt" style={{ ["--cols" as string]: cols }}>
        <div className="g-head">
          <div className="g-corner">Project · remaining</div>
          {labels.map((l) => <div className="g-wk" key={l}>{l}</div>)}
        </div>

        {rows.map(({ project, items, remaining, due: dueDate, families }) => {
          const env = envelope(items);
          const late = dueDate ? dueDate < board.today : false;
          const isOpen = openProjects.has(project.projectId);
          return (
            <div key={project.projectId}>
              <div className="g-row lvl0" onClick={() => toggle(openProjects, project.projectId, setOpenProjects)} style={{ cursor: "pointer" }}>
                <div className="g-name">
                  <b>{isOpen ? "▾ " : "▸ "}{project.projectId} {project.description?.slice(0, 20)}</b>
                  <span>
                    {Math.round(remaining)} h · {families.size} stage{families.size === 1 ? "" : "s"} ·{" "}
                    <span className={late ? "g-util over" : ""}>due {dueDate ?? "—"}{late ? " late" : ""}</span>
                  </span>
                </div>
                <div className="g-lane">
                  {env && <div className="g-env" style={{ gridColumn: `${env.first} / span ${env.span}` }} />}
                  {/* The project's earliest promise, against the whole envelope. */}
                  {(() => {
                    const lastEnd = items.reduce<number | null>(
                      (n, a) => (a.endBlock === null ? n : Math.max(n ?? a.endBlock, a.endBlock)),
                      null,
                    );
                    const d = due(dueDate, lastEnd, board.fromBlock, cols);
                    if (!d || d.col === null || !d.late) return null;
                    return (
                      <div
                        className="g-due"
                        style={{ gridColumn: d.col }}
                        title={`${project.projectId} — ${dueTitle(d, lastEnd !== null, { projectDue: project.committedDate })}`}
                      />
                    );
                  })()}
                </div>
              </div>

              {isOpen && [...families.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([fam, famItems]) => {
                const key = `${project.projectId}/${fam}`;
                const famOpen = openFamilies.has(key) || famItems.length === 1;
                const fenv = envelope(famItems);
                const famHours = famItems.reduce((n, a) => n + (phases.get(a.phaseId)?.remainingHours ?? 0), 0);
                return (
                  <div key={key}>
                    {famItems.length > 1 && (
                      <div className="g-row lvl1" onClick={() => toggle(openFamilies, key, setOpenFamilies)} style={{ cursor: "pointer" }}>
                        <div className="g-name sub">
                          <b>{famOpen ? "▾ " : "▸ "}{fam}</b>
                          <span>{famItems.length} steps · {Math.round(famHours)} h</span>
                        </div>
                        <div className="g-lane">
                          {fenv && <div className="g-env fam" style={{ gridColumn: `${fenv.first} / span ${fenv.span}` }} />}
                        </div>
                      </div>
                    )}
                    {famOpen && famItems.map((a) => {
                      const pos = place(a, board.fromBlock, cols);
                      const phase = phases.get(a.phaseId);
                      const who = a.resourceId ? resources.get(a.resourceId) : "unassigned";
                      const d = due(phase?.reqDueDate, a.endBlock, board.fromBlock, cols);
                      return (
                        <div
                          className="g-row lvl2" key={a.assignmentId}
                          onDoubleClick={phase && onOpenProject ? () => onOpenProject(phase.projectId) : undefined}
                          title={onOpenProject ? "Double-click to open the project and edit this step" : undefined}
                        >
                          <div className="g-name sub2">
                            <b>{phase?.phaseCode ?? stepLabel(phase, a.phaseId)}</b>
                            <span>{who}</span>
                          </div>
                          <div className="g-lane">
                            {pos && (
                              <div
                                className={`g-bar ${laneClass(a.priority, !a.resourceId)}${statusClass(phase?.workflowState)}${pos.clippedLeft ? " clip-l" : ""}${pos.clippedRight ? " clip-r" : ""}${d?.late ? " late" : ""}`}
                                style={{ gridColumn: `${pos.start} / span ${pos.span}` }}
                                title={`${stepLabel(phase, a.phaseId)} · ${phase?.description ?? ""} · ${phase?.remainingHours ?? 0}h · ${who}${statusTitle(phase?.workflowState)}${d ? ` · ${dueTitle(d, a.endBlock !== null, { finish: a.endDate ?? null, projectDue: projectDueOf.get(phase?.projectId ?? "") ?? null })}` : ""}`}
                              >
                                {d?.late ? "⚠ " : ""}{phase?.remainingHours ?? 0}h · {who}
                              </div>
                            )}
                            {!pos && phase?.overEstimate && (
                              <div
                                className="g-bar g-overbar"
                                style={{ gridColumn: "1 / span 6" }}
                                title={`${stepLabel(phase, a.phaseId)} · at or past its estimate and still open, so it has no bar. Re-estimate or complete it.`}
                              >
                                ⚑ over estimate
                              </div>
                            )}
                            {d && d.col !== null && d.late && (
                              <div
                                className="g-due"
                                style={{ gridColumn: d.col }}
                                title={`${stepLabel(phases.get(a.phaseId), a.phaseId)} — ${dueTitle(d, a.endBlock !== null, { finish: a.endDate ?? null, projectDue: projectDueOf.get(phase?.projectId ?? "") ?? null })}`}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="legend">
        <span>Click a project, then a stage, to drill in</span>
        <span>Gray bar = span from first start to last finish</span>
        <span>Notched edge = the bar continues past the visible window</span>
        <span>┆ required-by date — red where the schedule misses it</span>
        <span>⚠ finishes after its required date</span>
        <span>⚑ over estimate = open, no hours left, so no bar</span>
        <StatusKey />
      </div>
    </div>
  );
}
